/**
 * Search results, with facets.
 *
 * The URL is the state. Every filter lives in the query string, so a result set
 * can be bookmarked, shared and reloaded — which is the whole point of a
 * reference tool, and something a component-state implementation quietly loses.
 *
 * Result cards live in `components/search/ResultCards.tsx` and pagination in
 * `components/ui/Pagination.tsx`, so this file stays about state: what is
 * filtered, how it is sorted, and what to show when nothing matches.
 */

import { useCallback, useMemo } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { SearchBar } from '@/components/search/SearchBar'
import { BusinessCard, ProductCard, ServiceCard } from '@/components/search/ResultCards'
import { Pagination } from '@/components/ui/Pagination'
import { CloseIcon, FilterIcon, SearchIcon } from '@/components/ui/icons'
import { Button, cx, EmptyState, Select, Skeleton } from '@/components/ui/primitives'
import { searchApi } from '@/services/endpoints'
import type { SearchParams } from '@/services/endpoints'
import { formatNumber, humanise } from '@/utils/format'
import type { BusinessSummary, ProductSummary, SearchMeta, Service } from '@/types/api'

const SORTS = {
  business: [
    { value: 'relevance', label: 'Most relevant' },
    { value: 'trust', label: 'Best corroborated' },
    { value: 'quality', label: 'Most complete' },
    { value: 'updated', label: 'Recently updated' },
    { value: 'name', label: 'Name (A–Z)' },
  ],
  product: [
    { value: 'relevance', label: 'Most relevant' },
    { value: 'price_asc', label: 'Price: low to high' },
    { value: 'price_desc', label: 'Price: high to low' },
    { value: 'suppliers', label: 'Most suppliers' },
  ],
  service: [{ value: 'relevance', label: 'Most relevant' }],
} as const

const ENTITY_LABEL = {
  business: 'Businesses',
  product: 'Products',
  service: 'Services',
} as const

export function SearchPage() {
  const [params, setParams] = useSearchParams()

  const entity = (params.get('type_of') ?? 'business') as 'business' | 'product' | 'service'
  const query = params.get('q') ?? ''
  const page = Number(params.get('page') ?? 1)

  const searchParams = useMemo<SearchParams>(
    () => ({
      q: query || undefined,
      type_of: entity,
      state: params.get('state') ?? undefined,
      lga: params.get('lga') ?? undefined,
      category: params.get('category') ?? undefined,
      type: params.get('type') ?? undefined,
      verified: params.get('verified') === 'true' || undefined,
      has_price: params.get('has_price') === 'true' || undefined,
      sort: params.get('sort') ?? undefined,
      page,
    }),
    [entity, page, params, query],
  )

  const results = useQuery({
    queryKey: ['search', searchParams],
    queryFn: () => searchApi.search(searchParams),
    // Keeps the previous page on screen while the next loads. Without it,
    // paging blanks the list and the page jumps to the top on every click.
    placeholderData: keepPreviousData,
  })

  const facets = useQuery({ queryKey: ['facets'], queryFn: searchApi.facets })

  /** Set or clear one parameter, always resetting to page 1. */
  const update = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params)
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
      // Staying on page 7 after narrowing to 3 results shows an empty page.
      next.delete('page')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const clearAll = useCallback(() => {
    const next = new URLSearchParams()
    if (query) next.set('q', query)
    if (entity !== 'business') next.set('type_of', entity)
    setParams(next, { replace: true })
  }, [entity, query, setParams])

  const meta = results.data?.meta as SearchMeta | undefined
  const activeFilters = Object.entries({
    state: params.get('state'),
    lga: params.get('lga'),
    category: params.get('category'),
    type: params.get('type'),
    verified: params.get('verified'),
    has_price: params.get('has_price'),
  }).filter(([, value]) => value)

  const facetControls = (
    <div className="space-y-5">
            <FacetGroup
              title="State"
              buckets={facets.data?.states ?? []}
              active={params.get('state')}
              onSelect={(value) => update('state', value)}
              loading={facets.isLoading}
            />
            <FacetGroup
              title="Industry"
              buckets={facets.data?.categories ?? []}
              active={params.get('category')}
              onSelect={(value) => update('category', value)}
              loading={facets.isLoading}
            />
            {entity === 'business' ? (
              <FacetGroup
                title="Business type"
                buckets={
                  facets.data?.business_types.map((bucket) => ({
                    name: humanise(bucket.value),
                    slug: bucket.value,
                    count: bucket.count,
                  })) ?? []
                }
                active={params.get('type')}
                onSelect={(value) => update('type', value)}
                loading={facets.isLoading}
              />
            ) : null}

            <div>
              <p className="field-label mb-2">Confidence</p>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  checked={params.get('verified') === 'true'}
                  onChange={(event) => update('verified', event.target.checked ? 'true' : null)}
                  className="size-3.5 rounded border-[var(--border-default)] accent-[var(--accent)]"
                />
                Verified only
              </label>
              {entity === 'product' ? (
                <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={params.get('has_price') === 'true'}
                    onChange={(event) => update('has_price', event.target.checked ? 'true' : null)}
                    className="size-3.5 rounded border-[var(--border-default)] accent-[var(--accent)]"
                  />
                  Has a published price
                </label>
              ) : null}
            </div>
    </div>
  )

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <SearchBar className="mb-5 md:hidden" initialValue={query} />

      {/* Entity switch and sort */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-panel)] p-0.5">
          {(['business', 'product', 'service'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => update('type_of', value === 'business' ? null : value)}
              className={cx(
                'rounded-md px-3 py-1.5 text-sm transition-colors',
                entity === value
                  ? 'bg-[var(--surface-active)] font-medium text-[var(--text-primary)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              )}
            >
              {ENTITY_LABEL[value]}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <Select
          value={params.get('sort') ?? 'relevance'}
          onChange={(event) => update('sort', event.target.value)}
          fullWidth={false}
          className="min-w-44"
          aria-label="Sort results"
        >
          {SORTS[entity].map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Result summary */}
      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-sm text-[var(--text-secondary)]">
          {results.isLoading ? (
            'Searching…'
          ) : (
            <>
              <span className="font-semibold tabular text-[var(--text-primary)]">
                {formatNumber(meta?.total ?? 0)}
              </span>{' '}
              {meta?.total === 1 ? 'result' : 'results'}
              {query ? (
                <>
                  {' '}
                  for “<span className="text-[var(--text-primary)]">{query}</span>”
                </>
              ) : null}
            </>
          )}
        </p>
        {/* Query time is shown deliberately: this is an instrument, and an
            instrument should say how fast it answered. */}
        {meta?.took_ms !== undefined ? (
          <span className="text-2xs tabular text-[var(--text-tertiary)]">{meta.took_ms} ms</span>
        ) : null}
      </div>

      {activeFilters.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <FilterIcon size={13} className="text-[var(--text-tertiary)]" />
          {activeFilters.map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => update(key, null)}
              className="inline-flex items-center gap-1 rounded-full border border-[var(--accent-border)] bg-[var(--accent-subtle)] px-2 py-0.5 text-2xs text-[var(--accent-text)] transition-opacity hover:opacity-80"
            >
              {humanise(key)}: {value === 'true' ? 'yes' : humanise(value)}
              <CloseIcon size={10} />
            </button>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-2xs text-[var(--text-tertiary)] underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        {/* Facet sidebar */}
        {/* Facets: a sidebar where there is room, a disclosure where there is
            not. They used to be display:none below lg with NO substitute — on
            a search-first product, phone users simply had no filters. */}
        <details className="group rounded-lg border border-[var(--border-default)] bg-[var(--surface-panel)] lg:hidden">
          <summary className="flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm font-medium text-[var(--text-primary)] [&::-webkit-details-marker]:hidden">
            Filters
            <span className="text-xs text-[var(--text-tertiary)] group-open:hidden">show</span>
            <span className="hidden text-xs text-[var(--text-tertiary)] group-open:inline">hide</span>
          </summary>
          <div className="border-t border-[var(--border-subtle)] p-4">{facetControls}</div>
        </details>

        <aside className="hidden lg:block">
          <div className="sticky top-20">{facetControls}</div>
        </aside>

        {/* Results */}
        <div>
          {results.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-24" />
              ))}
            </div>
          ) : results.isError ? (
            <EmptyState
              icon={<SearchIcon size={18} />}
              title="Search is unavailable"
              description="Something went wrong on our side. Try again in a moment."
              action={
                <Button variant="secondary" size="sm" onClick={() => results.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : (meta?.total ?? 0) === 0 ? (
            <ZeroResults query={query} suggestions={meta?.suggestions ?? []} onClear={clearAll} />
          ) : (
            <>
              <div className={cx('space-y-3', results.isPlaceholderData && 'opacity-60')}>
                {entity === 'business'
                  ? (results.data?.data as BusinessSummary[]).map((item) => (
                      <BusinessCard key={item.uuid} business={item} />
                    ))
                  : entity === 'product'
                    ? (results.data?.data as ProductSummary[]).map((item) => (
                        <ProductCard key={item.uuid} product={item} />
                      ))
                    : (results.data?.data as Service[]).map((item) => (
                        <ServiceCard key={item.uuid} service={item} />
                      ))}
              </div>

              {meta && meta.pages > 1 ? (
                <Pagination
                  page={meta.page}
                  pages={meta.pages}
                  onChange={(next) => update('page', String(next))}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* -- Facets ---------------------------------------------------------------- */
function FacetGroup({
  title, buckets, active, onSelect, loading,
}: {
  title: string
  buckets: Array<{ name?: string; slug?: string; count: number }>
  active: string | null
  onSelect: (value: string | null) => void
  loading?: boolean
}) {
  if (loading) {
    return (
      <div>
        <p className="field-label mb-2">{title}</p>
        <div className="space-y-1.5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-4" />
          ))}
        </div>
      </div>
    )
  }

  if (buckets.length === 0) return null

  return (
    <div>
      <p className="field-label mb-2">{title}</p>
      <ul className="space-y-0.5">
        {buckets.slice(0, 10).map((bucket) => {
          const isActive = active === bucket.slug
          return (
            <li key={bucket.slug}>
              <button
                type="button"
                onClick={() => onSelect(isActive ? null : (bucket.slug ?? null))}
                className={cx(
                  'flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--accent-subtle)] font-medium text-[var(--accent-text)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]',
                )}
              >
                <span className="truncate">{bucket.name}</span>
                <span className="shrink-0 text-2xs tabular text-[var(--text-tertiary)]">
                  {bucket.count}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/* -- Zero results ---------------------------------------------------------- */
/**
 * An empty page that offers nothing is where a session ends — so this offers
 * the server's own suggestions and a way out of an over-narrow filter.
 */
function ZeroResults({
  query, suggestions, onClear,
}: {
  query: string
  suggestions: string[]
  onClear: () => void
}) {
  return (
    <EmptyState
      icon={<SearchIcon size={18} />}
      title={query ? `Nothing found for “${query}”` : 'No results'}
      description={
        <div className="space-y-3">
          <p>
            The index does not yet cover this. Searches that return nothing are recorded and
            used to decide what to crawl next.
          </p>
          {suggestions.length > 0 ? (
            <div>
              <p className="mb-1.5 text-xs text-[var(--text-tertiary)]">Related industries:</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {suggestions.map((suggestion) => (
                  <Link
                    key={suggestion}
                    to={`/search?q=${encodeURIComponent(suggestion)}`}
                    className="rounded-full border border-[var(--border-subtle)] px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:border-[var(--accent-border)] hover:text-[var(--accent-text)]"
                  >
                    {suggestion}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      }
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      }
    />
  )
}
